import { useEffect, useLayoutEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useContextStore } from '../hooks/context/useContextStore';
import { GET_CLIENT_ACCOUNT_CRISP_IDENTITY } from '../graphql/client/queries';

const CRISP_WEBSITE_ID = '984b49c7-e6df-46b0-86d9-b858e3ad8d56';
const CRISP_LOADER_URL = 'https://client.crisp.chat/l.js';

type CrispCommand = [string, string, unknown?];

declare global {
  interface Window {
    $crisp?: CrispCommand[];
    CRISP_WEBSITE_ID?: string;
    CRISP_READY_TRIGGER?: () => void;
  }
}

const pushCrispCommand = (command: CrispCommand) => {
  window.$crisp = window.$crisp ?? [];
  window.$crisp.push(command);
};

export default function CrispChat() {
  const { clientUser } = useContextStore();
  const { data } = useQuery(GET_CLIENT_ACCOUNT_CRISP_IDENTITY, { skip: !clientUser });
  const clientAccount = data?.getClientAccount?.account;

  useLayoutEffect(() => {
    if (!clientUser) return;

    window.$crisp = window.$crisp ?? [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  }, [clientUser]);

  useEffect(() => {
    if (!clientUser) return;

    if (document.head.querySelector('script[data-crisp-loader]')) return;

    const script = document.createElement('script');
    script.dataset.crispLoader = 'true';
    script.src = CRISP_LOADER_URL;
    script.async = true;
    document.head.appendChild(script);
  }, [clientUser]);

  useEffect(() => {
    if (!clientUser) return;

    const fullName = [clientUser.firstName, clientUser.lastName].filter(Boolean).join(' ');

    pushCrispCommand(['set', 'user:nickname', [fullName]]);
    pushCrispCommand(['set', 'user:email', [clientUser.email]]);

    if (clientUser.phone) {
      pushCrispCommand(['set', 'user:phone', [clientUser.phone]]);
    }

    if (!clientAccount) return;

    const accountId = clientUser.account_id ?? clientAccount.id;

    pushCrispCommand(['set', 'user:company', [clientAccount.companyName]]);
    pushCrispCommand(['set', 'session:segments', [['client']]]);
    pushCrispCommand([
      'set',
      'session:data',
      [
        [
          ['portal', 'client'],
          ['account_id', accountId],
        ],
      ],
    ]);
  }, [clientAccount, clientUser]);

  return null;
}
